/**
 * The Bullseye tab's server state: the plotted data (sub-task 01) and the
 * shared ring settings (sub-task 02), plus the debounced save.
 *
 * NO SILENT OVERWRITE OF A TEAMMATE. `PUT .../bullseye/settings` replaces the
 * whole row and the last writer wins, and a mode toggle has to resend the
 * boundaries. A tab left open for an afternoon would otherwise send the
 * boundaries it loaded at lunch over the ones a teammate saved since. So:
 *
 *   - local edits are kept as a PATCH (only the fields this user changed),
 *     never as a copy of the whole row;
 *   - right before each save the settings are read again, and the patch is
 *     laid over THAT row, so a mode toggle carries the latest saved bounds;
 *   - the boundaries are ONE field holding every ring, so laying them over a
 *     newer row would still overwrite a teammate's other rings. The patch
 *     therefore remembers the saved boundaries it started from, and when the
 *     re-read shows they changed since, this user's boundaries are NOT sent:
 *     the teammate's row is adopted and the tab says so (`conflict`);
 *   - the settings are read again when the browser tab regains focus or
 *     becomes visible (`refetchOnWindowFocus`, which TanStack drives from
 *     `visibilitychange`), so the screen catches up with a teammate too.
 *
 * A failed save keeps the patch, so the values on screen stay what the user
 * set, and says what went wrong (a 422's domain rule verbatim, field errors
 * mapped to the boundary they name). A save that fails after the tab was left
 * can no longer show a banner; it raises a toast instead.
 */
import type {
  BullseyeSettings,
  SearchBullseye,
  UpdateBullseyeSettingsRequest,
} from "@nu-assist/contracts";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import { BullseyeApiError, type BullseyeClient } from "../../../../clients/bullseye.client";
import { nameFromEmail } from "./bullseyeFormat";
import { DEFAULT_BOUNDS, type RingMode } from "./bullseyeGeometry";

/** Wait after the last change before saving, so a slider drag is one PUT. */
export const SAVE_DEBOUNCE_MS = 600;
/** How long "Saved" stays on screen. */
export const SAVED_NOTICE_MS = 2500;

export const bullseyeKey = (searchId: string) => ["search-bullseye", searchId] as const;
export const bullseyeSettingsKey = (searchId: string) =>
  ["search-bullseye-settings", searchId] as const;

export function useBullseyeData(client: BullseyeClient, searchId: string) {
  return useQuery<SearchBullseye>({
    queryKey: bullseyeKey(searchId),
    queryFn: () => client.get(searchId),
    enabled: searchId !== "",
  });
}

export type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

export interface SaveError {
  /** One sentence for the banner. */
  message: string;
  /** Field errors that name one boundary, by its index. */
  boundErrors: Map<number, string>;
}

/** What a failed save says. A 422 string is a domain rule and is shown as-is. */
export function describeSaveError(err: unknown): SaveError {
  const boundErrors = new Map<number, string>();
  if (err instanceof BullseyeApiError) {
    if (typeof err.detail === "string") return { message: err.detail, boundErrors };
    const general: string[] = [];
    for (const fe of err.fieldErrors) {
      const loc = fe.loc ?? [];
      const at = loc.indexOf("ring_bounds");
      const index = at >= 0 ? loc[at + 1] : undefined;
      const msg = fe.msg ?? "is not valid";
      if (typeof index === "number") boundErrors.set(index, msg);
      else general.push(at >= 0 ? `Ring boundaries: ${msg}` : msg);
    }
    if (boundErrors.size || general.length) {
      const rings = [...boundErrors].map(([i, msg]) => `Ring ${i + 1}: ${msg}`);
      return { message: [...rings, ...general].join(". "), boundErrors };
    }
    return { message: `The settings could not be saved (error ${err.status}).`, boundErrors };
  }
  return {
    message: "The settings could not be saved. Check your connection and try again.",
    boundErrors,
  };
}

export interface BullseyeSettingsState {
  /** The saved row, `undefined` while the first read is in flight. */
  saved: BullseyeSettings | undefined;
  isLoading: boolean;
  isFetching: boolean;
  loadError: unknown;
  /** What the tab shows: the saved row with this user's unsaved changes on top. */
  ringMode: RingMode;
  ringBounds: number[];
  status: SaveStatus;
  saveError: SaveError | null;
  /** Set when a teammate's boundaries were kept over this user's unsaved ones. */
  conflict: string | null;
  /** Read the settings again (the retry after a failed load). */
  reload: () => void;
  /** Change the mode and/or the boundaries; saved after `SAVE_DEBOUNCE_MS`. */
  update: (change: Partial<UpdateBullseyeSettingsRequest>) => void;
  /** Save now (the retry after a failure). */
  retry: () => void;
}

export function useBullseyeSettings(
  client: BullseyeClient,
  searchId: string
): BullseyeSettingsState {
  const queryClient = useQueryClient();
  const query = useQuery<BullseyeSettings>({
    queryKey: bullseyeSettingsKey(searchId),
    queryFn: () => client.getSettings(searchId),
    enabled: searchId !== "",
    // The app turns focus refetch off globally; this read is the exception
    // (see the header note).
    refetchOnWindowFocus: true,
    staleTime: 0,
  });

  const [patch, setPatch] = useState<Partial<UpdateBullseyeSettingsRequest>>({});
  const patchRef = useRef(patch);
  // The saved boundaries this user's boundary edit started from.
  const boundsBase = useRef<readonly number[] | null>(null);
  const [conflict, setConflict] = useState<string | null>(null);
  const [status, setStatus] = useState<SaveStatus>("idle");
  const [saveError, setSaveError] = useState<SaveError | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Saves run one after another, never two PUTs at once.
  const chain = useRef<Promise<void>>(Promise.resolve());
  const live = useRef(true);
  // A save is running (re-read + PUT); leaving the tab then must not queue a
  // second one for the same patch.
  const inFlight = useRef(false);
  // The last save was refused (4xx) and the user saw why: leaving the tab
  // must not retry a request that is certain to be refused again.
  const refused = useRef(false);

  const flush = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    chain.current = chain.current.then(async () => {
      const sent = patchRef.current;
      if (sent.ring_mode === undefined && sent.ring_bounds === undefined) return;
      if (live.current) setStatus("saving");
      inFlight.current = true;
      try {
        // Re-read right before saving: the patch goes over the LATEST row.
        const latest = await client.getSettings(searchId);
        const base = boundsBase.current;
        // Overtaken = the saved bounds moved since this edit began AND they are
        // not this user's own (a save whose response was lost, a tab left and
        // reopened within the debounce): a user is never told they overwrote
        // themselves.
        const overtaken =
          sent.ring_bounds !== undefined &&
          base !== null &&
          !sameBounds(base, latest.ring_bounds) &&
          !sameBounds(sent.ring_bounds, latest.ring_bounds);
        let saved = latest;
        if (sent.ring_mode !== undefined || (sent.ring_bounds !== undefined && !overtaken)) {
          saved = await client.saveSettings(searchId, {
            ring_mode: sent.ring_mode ?? latest.ring_mode,
            ring_bounds: overtaken ? latest.ring_bounds : (sent.ring_bounds ?? latest.ring_bounds),
          });
        }
        // A read already in flight would land after this and bring the old row back.
        await queryClient.cancelQueries({ queryKey: bullseyeSettingsKey(searchId) });
        queryClient.setQueryData(bullseyeSettingsKey(searchId), saved);
        // Edits made while this save was in flight now start from what was
        // just saved: this user's own save must not read as a teammate's.
        // Only when THIS save carried this user's bounds: after a mode-only
        // save, `saved.ring_bounds` may be a teammate's the user never saw,
        // and rebasing on them would let the next save overwrite them.
        if (
          !overtaken &&
          sent.ring_bounds !== undefined &&
          patchRef.current !== sent &&
          patchRef.current.ring_bounds !== undefined
        ) {
          boundsBase.current = saved.ring_bounds;
        }
        if (overtaken && patchRef.current !== sent) {
          // The teammate's boundaries win, including over a drag made since;
          // anything else changed since stays pending (its timer is running).
          const { ring_bounds: _dropped, ...rest } = patchRef.current;
          patchRef.current = rest;
          boundsBase.current = null;
          if (live.current) setPatch(rest);
        }
        // A change made while this save was in flight stays pending.
        if (patchRef.current === sent) {
          patchRef.current = {};
          boundsBase.current = null;
          if (live.current) setPatch({});
        }
        const notice = overtaken
          ? `${nameFromEmail(latest.updated_by) || "A teammate"} changed the ring boundaries while you were editing. Their boundaries are shown; yours were not saved.`
          : null;
        refused.current = false;
        if (live.current) {
          setSaveError(null);
          setConflict(notice);
          // "Saved" next to "yours were not saved" would contradict it.
          const idle = Object.keys(patchRef.current).length === 0;
          setStatus(idle ? (overtaken ? "idle" : "saved") : "pending");
        } else if (notice) {
          toast.warning(notice);
        }
      } catch (err) {
        refused.current = err instanceof BullseyeApiError && err.status >= 400 && err.status < 500;
        const described = describeSaveError(err);
        if (live.current) {
          setSaveError(described);
          setStatus("error");
        } else {
          toast.error(`The Bullseye ring settings were not saved: ${described.message}`);
        }
      } finally {
        inFlight.current = false;
      }
    });
  }, [client, searchId, queryClient]);

  const update = useCallback(
    (change: Partial<UpdateBullseyeSettingsRequest>) => {
      setConflict(null);
      refused.current = false;
      if (change.ring_bounds !== undefined && patchRef.current.ring_bounds === undefined) {
        // Read from the cache, not from a render-time copy: a save that just
        // wrote the cache is already there, one commit before React re-renders.
        const current = queryClient.getQueryData<BullseyeSettings>(bullseyeSettingsKey(searchId));
        boundsBase.current = current?.ring_bounds ?? [...DEFAULT_BOUNDS];
      }
      const next = { ...patchRef.current, ...change };
      patchRef.current = next;
      setPatch(next);
      setStatus("pending");
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(flush, SAVE_DEBOUNCE_MS);
    },
    [flush, queryClient, searchId]
  );

  // Leaving the tab with a change not yet saved (waiting on the debounce, or
  // left behind by a save that failed for a reason worth retrying) saves it
  // instead of losing it. Not while a save is running (it already carries the
  // patch), and not after a refusal the user has already read.
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
      const leftOver =
        !inFlight.current && !refused.current && Object.keys(patchRef.current).length > 0;
      if (timer.current || leftOver) flush();
    };
  }, [flush]);

  useEffect(() => {
    if (status !== "saved") return;
    const t = setTimeout(() => setStatus((s) => (s === "saved" ? "idle" : s)), SAVED_NOTICE_MS);
    return () => clearTimeout(t);
  }, [status]);

  const saved = query.data;
  const ringMode = patch.ring_mode ?? saved?.ring_mode ?? "ai_score";
  const ringBounds = useMemo(
    () => patch.ring_bounds ?? saved?.ring_bounds ?? [...DEFAULT_BOUNDS],
    [patch.ring_bounds, saved?.ring_bounds]
  );

  return {
    saved,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    loadError: query.error,
    ringMode,
    ringBounds,
    status,
    saveError,
    conflict,
    reload: () => {
      void query.refetch();
    },
    update,
    retry: flush,
  };
}

function sameBounds(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((v, i) => Math.abs(v - (b[i] ?? Number.NaN)) < 1e-9);
}
